import {Page} from './constant/page';
import {getEnv} from './env';
import {Badge, BadgeType, IEpisodeSort, IRegion} from './type';

export const IS_CLIENT = typeof window !== 'undefined';
export const IS_DEBUG = 'IS_DEBUG';
export const WEB_HOST = getEnv('WEB_HOST')!;
export const API_HOST = getEnv('API_HOST')!;
export const AUTH_HOST = getEnv('AUTH_HOST')!;
export const BILLING_WEB_HOST = getEnv('BILLING_WEB_HOST')!;
export const ENV_NAME = getEnv('ENV_NAME')!;
export const SENTRY_DSN = getEnv('SENTRY_DSN')!;
export const VERSION = getEnv('VERSION')!;
export const REGION: IRegion = getEnv('REGION')! as IRegion;
export const IS_PROD = ENV_NAME === 'prod';
const IS_CBT = ENV_NAME === 'cbt';
export const IS_CBT_OR_PROD = IS_CBT || IS_PROD;
export const IS_QA = ENV_NAME === 'qa';
export const IS_LOCAL = ENV_NAME.includes('local');
export const NEED_TRACKING = IS_PROD;
export const ROOT_ELEMENT_ID = 'root';
export const ACCESS_TOKEN_KEY = 'atn';
export const ACCESS_TOKEN_REFRESH_TIME_KEY = 'refresh-time';
export const NOTICE_POPUP_KEY = 'notice-popup-id';
export const EPISODE_SORT_KEY = 'episode-sort';
export const RECENT_ITEM_LIST_KEY = 'recent-list';
export const CLOSED_WEB_TO_APP_BANNER_KEY = 'closed-web-to-app';
export const SHOWED_WAITFREE_RED_DOT_KEY = 'showed-waitfree-red-dot';
export const APP_ONE_LINK = 'https://kakaowebtoon.onelink.me/YAuT/9d2030ac';
export const GIFT_LAST_DATE_TIME_KEY = 'gift-last-read-date-time';
export const MORE_RED_DOT_LAST_DATE_TIME_KEY =
  'more-red-dot-last-read-date-time';

export const UNKNOWN = 'unknown';

// 조건절 편의를 위해 1부터 시작하도록 함, ex) predefinedCallback ? A : B
export enum AlertPredefinedCallbackWithSSR {
  GoToHome = 1,
  GoBack,
}

// passive 속성값 feature detection
let supportsPassive = false;
try {
  const opts = Object.defineProperty({}, 'passive', {
    get() {
      supportsPassive = true;
    },
  });
  if (IS_CLIENT) {
    window.addEventListener('testPassive', null as any, opts);
    window.removeEventListener('testPassive', null as any, opts);
  }
} catch (e) {
  /* empty */
}
// 가능하다면 touchstart 이벤트를 붙일때 passive 속성값을 준다
export const PASSIVE_TOUCH_IF_POSSIBLE: AddEventListenerOptions | boolean =
  supportsPassive ? {passive: true} : false;
export const DISABLE_PASSIVE_TOUCH_IF_POSSIBLE:
  | AddEventListenerOptions
  | boolean = supportsPassive ? {passive: false} : false;

export enum KeyboardKeyType {
  None,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowDown,
  Space,
  Escape,
  Enter,
  PageUp,
  PageDown,
}

// https://www.iana.org/assignments/language-subtag-registry/language-subtag-registry
export const LANGUAGE_TAG_FOR_LOCALE_STRING: {[key in IRegion]: string} = {
  kor: 'ko',
  twn: 'zh-Hant',
  tha: 'th',
  idn: 'id',
};
export const LOCALE: {[key in IRegion]: string} = {
  kor: 'ko_KR',
  twn: 'zh_TW',
  tha: 'th_TH',
  idn: 'id_ID',
};

export const APP_SCHEME = 'kakaowebtoon';

export const DEFAULT_EPISODE_SORT: IEpisodeSort = 'latest';

export const DEFAULT_RATING = 10;

export const NICKNAME_MIN_LENGTH = 2;
export const NICKNAME_MAX_LENGTH = 20;

// 이 부분 수정되면 DOMAIN_MAP 사용하는 곳 ENV_NAME 확인하자
export const DOMAIN_MAP: {[key in IRegion]: {[key in string]: string}} = {
  kor: {
    local: WEB_HOST,
    dev: WEB_HOST,
    qa: 'https://qa-webtoon.kakao.com',
    cbt: 'https://cbt-webtoon.kakao.com',
    prod: 'https://webtoon.kakao.com',
  },
  twn: {
    local: WEB_HOST,
    dev: WEB_HOST,
    qa: 'https://qa-tw.kakaowebtoon.com',
    cbt: 'https://cbt-tw.kakaowebtoon.com',
    prod: 'https://tw.kakaowebtoon.com',
  },
  tha: {
    local: WEB_HOST,
    dev: WEB_HOST,
    qa: 'https://qa-th.kakaowebtoon.com',
    cbt: 'https://cbt-th.kakaowebtoon.com',
    prod: 'https://th.kakaowebtoon.com',
  },
  idn: {
    local: WEB_HOST,
    dev: WEB_HOST,
    qa: 'https://qa-id.kakaowebtoon.com',
    cbt: 'https://cbt-id.kakaowebtoon.com',
    prod: 'https://id.kakaowebtoon.com',
  },
};

export enum AppUri {
  PointSetting = 'point/setting',
  Close = 'close',
  Sticker = 'whatsapp-stickers/[stickerId]',
}

export const SNS_IMAGE = `${WEB_HOST}/kakaowebtoon_meta.png`;
export const DEFAULT_ASPECT_RATIO = 244 / 122;
export const BIG_CONTENT_ASPECT_RATIO = 260 / 375;
export const CONTENT_CARD_WRAPPER_MARGIN_X_CN = '-mx-2';
export const CONTENT_CARD_THUMB_FIT_CN = 'object-cover object-top';

export const BILLING_PATH: {[key in IRegion]: string} = {
  kor: 'KW_KR',
  twn: 'KW_TW',
  tha: 'KW_TH',
  idn: 'KW_ID',
};

export const WEBTOON_LEAGUE_URL = IS_CBT_OR_PROD
  ? 'https://webtoonleague.kakao.com'
  : 'https://webtoonleague.devel.kakao.com';

export const BADGE_MAP: {[key in BadgeType]: Badge} = {
  episodesNotPublishing: {
    variant: 's10_grey09_white_dark',
    label: 'badge_hiatus',
  },
  episodesPublishing: {variant: 's10_red_white_dark', label: 'badge_ongoing'},
  endOfSeason: {
    variant: 's10_grey09_white_dark',
    label: 'badge_completed_season',
  },
  seasonCompleted: {
    variant: 's10_darkBlue_white',
    label: 'badge_completed_season',
  },
  completed: {variant: 's10_darkBlue_white', label: 'badge_completed'},
  stopSelling: {
    variant: 's10_grey09_white_dark',
    label: 'badge_not_available',
  },
  alive: {variant: 's10_lightPink_white', label: 'badge_alive'},
  brand: {variant: 's10_lightPink_white', label: 'badge_brand'},
  new: {variant: 's10_red_white_dark', label: 'badge_new'},
  season: {
    variant: 's10_red_white_dark',
    label: 'badge_new_season',
    isEllipsis: true,
  },
  up: {variant: 's10_red_white_dark', label: 'badge_up'},
  '12': {variant: 's10_green_white', label: '12'},
  '15': {variant: 's10_blue_white', label: '15'},
  '19': {
    variant: undefined,
    label: REGION === 'kor' ? '성인' : '19',
    imgType: 'adult',
  },
  mon: {variant: 's10_white_black_03', label: 'badge_mon'},
  tue: {variant: 's10_white_black_03', label: 'badge_tue'},
  wed: {variant: 's10_white_black_03', label: 'badge_wed'},
  thu: {variant: 's10_white_black_03', label: 'badge_thu'},
  fri: {variant: 's10_white_black_03', label: 'badge_fri'},
  sat: {variant: 's10_white_black_03', label: 'badge_sat'},
  sun: {variant: 's10_white_black_03', label: 'badge_sun'},
  rental: {variant: 's10_red_white_dark', label: 'badge_rent'},
  possession: {variant: 's10_red_white_dark', label: 'badge_possess'},
  free: {variant: 's10_white_black_02', label: 'badge_free'},
  // 3다무 뱃지가 들어가면서 에피소드 목록에만 사용하던 기다무 뱃지가 작품 카드에도 사용된다.
  waitForFree: {label: 'viewer_ep_list_gidamu', imgType: 'waitForFree'},
  waitForFreeTimer: {
    label: 'common_now_charging',
    imgType: 'waitForFreeTimer',
  },
  waitForFreePlus: {
    label: 'common_gidamu_plus',
    imgTextType: 'waitForFreePlus',
  },
  waitForFreePlusTimer: {
    label: 'common_gidamu_plus',
    imgTextType: 'waitForFreePlusTimer',
  },
  waitForFreePlusExtended: {
    label: 'common_gidamu_plus_long',
    imgTextType: 'waitForFreePlusExtended',
  },
  waitForFreeExtended: {
    label: 'common_gidamu_plus_long',
    imgTextType: 'waitForFreeExtended',
  },
  waitForFreeWaiting: {
    label: 'common_gidamu',
    imgType: 'waitForFreeWaiting',
  },
  kakaopage: {
    label: 'kakaopage',
    imgType: 'kakaopage',
  },
  pageOriginalNovel: {
    variant: 's10_yellow_black',
    label: '원작소설', // 한국에서만 사용하기 때문에 번역키는 쓰지 않는다
  },
  freePublishing: {
    variant: 's10_yellow_darkGrey01',
    label: 'common_ongoing_free',
  },
  event: {
    variant: 's10_purple_white',
    label: 'recomm_label_event',
  },
};

export const X_PADDING_ONLY_SMALL_CN = 'px-20 lg:px-0';
export const X_MARGIN_ONLY_SMALL_CN = 'mx-20 lg:mx-0';

export const THEME_COOKIE_KEY = 'theme';
export const THEME_DATASET_KEY = 'theme';
export const DEFAULT_THEME = 'dark';

/**
 * 하단탭 스프린트 이후 light 테마일 때 background-02가 쓰일 수도 있다.
 * background-02가 쓰이는 페이지는 아래 배열에 추가해준다.
 */
const BACKGROUND_02_PAGES: Page[] = [
  Page.Dev,
  Page.ContentPurchase,
  Page.ContentTicketHistory,
  Page.ContentTicketRefund,
  Page.ContentNotice,
  Page.Luckydraw,
  Page.LuckydrawEnterinfo,
  Page.LuckydrawRaffle,
  ...Object.keys(Page)
    .filter(page => page.startsWith('More'))
    .map(page => Page[page as keyof typeof Page]),
];

export const getBgCN = (currentPage: Page) =>
  BACKGROUND_02_PAGES.includes(currentPage)
    ? 'bg-background-02'
    : 'bg-background';
